import { P } from '../components/Typography';

const colors = {
    background: '#fff', // white
    backgroundAlt: '#f5f7f8', // light grey
    backgroundInset: 'rgba(240, 249, 254, 0.27)', // transparent light grey

    borderLight: '#f2f2f2', // light grey
    borderDark: '#d8d8d8', // medium gray
    borderEmphasized: '#b8b8b8', // strong gray

    primary: '#0000ff', // blue
    primaryHover: 'rgba(0, 0, 255, 0.8)', // blue lighter
    primaryDisabled: 'rgba(132, 169, 240, 1.0)', // blue faded

    valid: 'rgba(8, 224, 133, 1.0)', // green
    invalid: '#EB001B', // red
    warning: '#f79e1b',

    // for interactive elements,  maybe move somewhere else
    primaryFocusOutline: 'rgba(0, 0, 255, 0.8)',
    interactiveBackgroundHover: 'rgba(0, 0, 255, 0.05)',
    interactiveBackgroundValid: 'rgba(27, 199, 126, 0.05)',
    interactiveBackgroundInvalid: 'rgb(189, 4, 75, 0.05)',

    text: '#000000', // asphalt gray
    textSecondary: '#333333', // asphalt gray

    secondary: {
        header: '#fff', // white
        child: '#02d682', // green
        background: '#5c6771', // dark grey
    },

    // light orange/brown hue for visual distinction in lists
    highlightAltBackground: '#fcf3e1',
    highlightAltBackgroundHover: '#fae5be',
};

// New palette added by Rob, should eventually replace most other colors
const palette = {
    primary: {
        '500': '#0000FF',
        '400': '#8C8CFF',
        '300': '#A6A6FF',
        '200': '#BFBFFF',
    },
    warning: {
        '500': '#F59E0B',
        '400': '#FBBF24',
        '300': '#FCD34D',
        '200': '#FDE68A',
    },
    success: {
        '500': '#22C55E',
        '400': '#4ADE80',
        '300': '#86EFAC',
        '200': '#BBF7D0',
    },
    destructive: {
        '500': '#EF4444',
        '400': '#F87171',
        '300': '#FCA5A5',
        '200': '#FECACA',
    },
    neutral: {
        '500': '#83839D',
        '400': '#C7C7D3',
        '300': '#D4D4DD',
        '200': '#E0E0E7',
    },
};

interface TransactionStates {
    [key: string]: string;
}
const transaction_states: TransactionStates = {
    INITIATED: 'rgba(0, 0, 255, 1.0)',
    ON_HOLD: 'rgba(188, 194, 197, 1.0)',
    DECLINED: '#EB001B',
    FAILED: '#EB001B',
    UNKNOWN: '#EB001B',
    AUTHORIZED: 'rgba(0, 145, 237, 1.0)',
    AUTHORIZATION_VOIDED: 'rgba(188, 194, 197, 1.0)',
    CAPTURED: 'rgba(0, 203, 124, 1.0)',
    PARTIALLY_CAPTURED: 'rgba(0, 203, 124, 1.0)',
    SETTLEMENT_COMPLETED: 'rgba(0, 203, 124, 1.0)',
    NOT_SETTLED: 'rgba(188, 194, 197, 1.0)',
    REFUNDED: 'rgba(157, 83, 157, 1.0)',
    PARTIALLY_REFUNDED: 'rgba(222, 157, 222, 1.0)',
    PARTIALLY_CAPTURED_REFUNDED: 'rgba(222, 157, 222, 1.0)',
};
type TransactionStatus = {
    [key: string]: {
        border: string;
        background: string;
        text: string;
    };
}
const transaction_status: TransactionStatus = {
    INITIATED: { border: '#0000ff', background: '#0000ff', text: '#ffffff' },
    ON_HOLD: { border: '#0000ff', background: '#ffffff', text: '#00006e' },
    DECLINED: { border: '#FFDBDB', background: '#FFDBDB', text: '#00006e' },
    FAILED: { border: '#F51D44', background: '#F51D44', text: '#ffffff' },
    AUTHORIZED: { border: '#0000ff', background: '#0000ff', text: '#ffffff' },
    AUTHORIZATION_VOIDED: { border: '#0000ff', background: '#ffffff', text: '#00006e' },
    CAPTURED: { border: '#48F4C1', background: '#48F4C1', text: '#00006e' },
    PARTIALLY_CAPTURED: { border: '#CEF8F1', background: '#CEF8F1', text: '#00006e' },
    REFUNDED: { border: '#F4EB4A', background: '#F4EB4A', text: '#00006e' },
    PARTIALLY_REFUNDED: { border: '#F3F0BF', background: '#F3F0BF', text: '#00006e' },
    PARTIALLY_CAPTURED_REFUNDED: { border: '#F3F0BF', background: '#F3F0BF', text: '#00006e' },
    UNKNOWN: { border: '#d8d8d8', background: '#ffffff', text: '#00006e' },
    SETTLEMENT_COMPLETED: { border: '#48F4C1', background: '#48F4C1', text: '#00006e' },
    NOT_SETTLED: { border: '#d8d8d8', background: '#ffffff', text: '#00006e' },
};

interface SessionStates {
    [key: string]: string;
}
const session_states: SessionStates = {
    LOADING: 'rgba(0, 145, 237, 1.0)', // focus blue
    INITIATED: 'rgba(0, 145, 237, 1.0)', // focus blue
    VISITED: 'rgba(0, 145, 237, 1.0)', // focus blue
    PRELOADED: 'rgba(0, 145, 237, 1.0)', // focus blue
    PUSH_NOTIFICATION_SENT: 'rgba(0, 145, 237, 1.0)', // focus blue
    COMPLETED: 'rgba(0, 145, 237, 1.0)', // focus blue
    AUTHORIZED: 'rgba(0, 203, 124, 1.0)', // green
    CAPTURED: 'rgba(0, 203, 124, 1.0)', // green
    EXPIRED: '#EFA51C', // yellow
    CANCELLED: '#EFA51C', // yellow
    CANCELED: '#EFA51C', // yellow
    PAY_FAILED: '#EFA51C', // yellow
    FAILED: '#EB001B', // red
    CAPTURE_FAILED: '#EB001B', // red
    UNKNOWN: '#EB001B', // red
    DECLINED: '#EB001B', // red
};

interface RequestStates {
    [key: string]: string;
}
const request_states: RequestStates = {
    FAILED: 'rgba(203, 0, 74, 1.0)',
    OK: 'rgba(0, 203, 124, 1.0)',
};

// the further out in the alphabet, the shorter the distance
const distances = {
    ginormous: '128px',
    large: '64px',
    normal: '32px',
    small: '16px',
    tiny: '8px',
    micro: '4px',
    nano: '2px',
};

const gridNumbers = {
    column: 80,
    gutter: 16,
};

const grid = {
    column: `${gridNumbers.column}px`,
    gutter: `${gridNumbers.gutter}px`,
    spans: {
        span1: `${gridNumbers.column}px`,
        span2: `${(gridNumbers.column + gridNumbers.gutter) * 2 - gridNumbers.gutter}px`,
        span3: `${(gridNumbers.column + gridNumbers.gutter) * 3 - gridNumbers.gutter}px`,
        span4: `${(gridNumbers.column + gridNumbers.gutter) * 4 - gridNumbers.gutter}px`,
        span5: `${(gridNumbers.column + gridNumbers.gutter) * 5 - gridNumbers.gutter}px`,
        span6: `${(gridNumbers.column + gridNumbers.gutter) * 6 - gridNumbers.gutter}px`,
        span7: `${(gridNumbers.column + gridNumbers.gutter) * 7 - gridNumbers.gutter}px`,
        span8: `${(gridNumbers.column + gridNumbers.gutter) * 8 - gridNumbers.gutter}px`,
        span9: `${(gridNumbers.column + gridNumbers.gutter) * 9 - gridNumbers.gutter}px`,
        span10: `${(gridNumbers.column + gridNumbers.gutter) * 10 - gridNumbers.gutter}px`,
        span11: `${(gridNumbers.column + gridNumbers.gutter) * 11 - gridNumbers.gutter}px`,
        span12: `${(gridNumbers.column + gridNumbers.gutter) * 12 - gridNumbers.gutter}px`,
    },
};
// Border thickness
const border = {
    normal: '2px',
    double: '4px',
};

const zIndex = {
    behind: -100,
    normal: 0,
    top: 1000,
};

export { colors, palette, distances, grid, zIndex, border, transaction_states, transaction_status, session_states, request_states };
